import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { RoomEvent } from 'livekit-client'
import { TrackSource } from '@livekit/protocol'

import { useRoomContext } from '@/src/context'

export function useParticipantPermissionsNotification() {
  const room = useRoomContext()
  const { t } = useTranslation()
  const previousCanPublishMicrophone = useRef<boolean | undefined>(undefined)
  const previousCanPublishScreenShare = useRef<boolean | undefined>(undefined)

  useEffect(() => {
    const handlePermissionsChanged = () => {
      const localParticipant = room.localParticipant
      const canPublish = localParticipant.permissions?.canPublish
      const canPublishSources = localParticipant.permissions?.canPublishSources
      const canPublishMicrophone =
        canPublish &&
        (!canPublishSources?.length ||
          canPublishSources.some((source) => source === TrackSource.MICROPHONE))
      const canPublishScreenShare =
        canPublish &&
        (!canPublishSources?.length ||
          canPublishSources.some((source) => source === TrackSource.SCREEN_SHARE))

      if (previousCanPublishMicrophone.current === undefined) {
        previousCanPublishMicrophone.current = canPublishMicrophone
      }

      if (previousCanPublishScreenShare.current === undefined) {
        previousCanPublishScreenShare.current = canPublishScreenShare
      }

      if (previousCanPublishMicrophone.current !== canPublishMicrophone) {
        if (canPublishMicrophone === false) {
          toast.info(t('micBlockedByAdmin'), {
            position: 'bottom-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            theme: 'dark',
          })
        }

        previousCanPublishMicrophone.current = canPublishMicrophone
      }

      if (previousCanPublishScreenShare.current !== canPublishScreenShare) {
        if (canPublishScreenShare === false) {
          toast.info(t('screenShareBlockedByAdmin'), {
            position: 'bottom-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            theme: 'dark',
          })
        }

        previousCanPublishScreenShare.current = canPublishScreenShare
      }
    }

    room.on(RoomEvent.ParticipantPermissionsChanged, handlePermissionsChanged)

    return () => {
      room.off(RoomEvent.ParticipantPermissionsChanged, handlePermissionsChanged)
    }
  }, [room, t])
}

